import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { communicationKeys } from "@/domains/communication/api/keys";
import type {
  EmailTemplate,
  ReceivedEmail,
  SentEmail,
} from "@/domains/communication/api/types";
import {
  fetchReceivedEmails,
  fetchReceivedEmailsByCase,
  fetchSentEmails,
  fetchSentEmailsByCase,
  fetchTemplateById,
  fetchTemplates,
} from "@/domains/communication/mocks/communicationService";

export const templatesListOptions = queryOptions({
  queryKey: communicationKeys.templates(),
  queryFn: fetchTemplates,
});

export const templateDetailOptions = (id: string) =>
  queryOptions({
    queryKey: communicationKeys.template(id),
    queryFn: () => fetchTemplateById(id),
  });

export const sentEmailsOptions = queryOptions({
  queryKey: communicationKeys.sent(),
  queryFn: fetchSentEmails,
});

export const sentEmailsByCaseOptions = (caseId: string) =>
  queryOptions({
    queryKey: communicationKeys.sentByCase(caseId),
    queryFn: () => fetchSentEmailsByCase(caseId),
  });

export function useTemplates(): UseQueryResult<ReadonlyArray<EmailTemplate>> {
  return useQuery(templatesListOptions);
}

export function useTemplate(id: string): UseQueryResult<EmailTemplate | undefined> {
  return useQuery(templateDetailOptions(id));
}

export function useSentEmails(): UseQueryResult<ReadonlyArray<SentEmail>> {
  return useQuery(sentEmailsOptions);
}

export function useSentEmailsByCase(
  caseId: string,
): UseQueryResult<ReadonlyArray<SentEmail>> {
  return useQuery(sentEmailsByCaseOptions(caseId));
}

export const receivedEmailsOptions = queryOptions({
  queryKey: communicationKeys.received(),
  queryFn: fetchReceivedEmails,
});

export const receivedEmailsByCaseOptions = (caseId: string) =>
  queryOptions({
    queryKey: communicationKeys.receivedByCase(caseId),
    queryFn: () => fetchReceivedEmailsByCase(caseId),
  });

export function useReceivedEmails(): UseQueryResult<ReadonlyArray<ReceivedEmail>> {
  return useQuery(receivedEmailsOptions);
}

export function useReceivedEmailsByCase(
  caseId: string,
): UseQueryResult<ReadonlyArray<ReceivedEmail>> {
  return useQuery(receivedEmailsByCaseOptions(caseId));
}
